import {
  MoviieProvider,
  MoviieVideo,
  PLAYER_API_EVENTS,
  useMoviieEvent,
  useMoviiePlaybackEnded,
  useMoviiePlayer,
} from '@moviie/player-expo'
import { useRef } from 'react'

import { IUpdateLessonProgress } from '@components/MediaRender'

// Mirrors the SparkVideos `VideoPlayer`: report the lesson once per quartile so
// the shared funnel auto-completes the lesson at 75%.
const PROGRESS_THRESHOLDS = [25, 50, 75, 100] as const

interface IMoviieLessonPlayerProps {
  /** Stable provider embed id (`Media.hostKey` for host=Moviie). */
  embedId: string
  /**
   * School-scoped publishable playback key (`Media.providerKey`). Required for
   * native playback; optional on web, where the package renders the official
   * watch embed in an iframe (it authorizes itself).
   */
  publishableKey?: string | null
  /**
   * Lesson-progress sink, identical in shape to the SparkVideos `VideoPlayer`.
   * Fires once per 25/50/75/100 threshold as the student watches. Native only:
   * on web the package runs inside the watch-embed iframe (no `player` instance),
   * so this is a no-op — matching SparkVideos, which also drops to an untracked
   * iframe on web.
   */
  updateProgress?: (params: IUpdateLessonProgress) => void
}

function PlayerSurface({
  embedId,
  updateProgress,
}: {
  embedId: string
  updateProgress?: (params: IUpdateLessonProgress) => void
}) {
  const moviie = useMoviiePlayer({ embedId })
  const { player, playback } = moviie
  const sentMarkersRef = useRef<Record<number, boolean>>({})

  const reportThreshold = (threshold: number, currentTime: number) => {
    if (sentMarkersRef.current[threshold]) return
    sentMarkersRef.current = { ...sentMarkersRef.current, [threshold]: true }
    updateProgress?.({
      progress: threshold,
      currentVideoTime: Math.floor(Math.max(currentTime, 0)),
    })
  }

  // Native time updates (~1s cadence). `player` is null on web, where
  // `useMoviieEvent` is a no-op, so web stays untracked just like SparkVideos.
  useMoviieEvent(player, PLAYER_API_EVENTS.TIME_UPDATE, () => {
    if (!player) return
    const duration = player.duration || playback?.durationSeconds || 0
    if (duration <= 0) return
    const currentTime = player.currentTime ?? 0
    const progress = (currentTime / duration) * 100
    PROGRESS_THRESHOLDS.forEach((threshold) => {
      if (progress >= threshold) reportThreshold(threshold, currentTime)
    })
  })

  // The engine's `currentTime` can settle just below `duration` at the end, so
  // the 100% marker may never trip via timeupdate alone. Guarantee it on the
  // natural playback end.
  useMoviiePlaybackEnded(player, () => {
    reportThreshold(100, player?.currentTime ?? playback?.durationSeconds ?? 0)
  })

  return <MoviieVideo {...moviie} aspectRatio={16 / 9} />
}

/**
 * Universal Moviie lesson player — one component, three platforms. The package
 * resolves the rendition per platform: expo-video + Moviie chrome on iOS and
 * Android, the official watch embed (iframe) on web. The provider is mounted
 * per-player because the key is per-school (it travels with the media payload,
 * not build-time env).
 */
export function MoviieLessonPlayer({
  embedId,
  publishableKey,
  updateProgress,
}: IMoviieLessonPlayerProps) {
  return (
    <MoviieProvider publishableKey={publishableKey ?? undefined}>
      <PlayerSurface embedId={embedId} updateProgress={updateProgress} />
    </MoviieProvider>
  )
}
